import { useState } from 'react'
import {
  Typography,
  Button,
  AppBar,
  IconButton,
  CircularProgress,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { BackButton } from '$common/buttons'
import { DoneIcon } from '$common/icons/done'
import { toLocale } from '$func/text'
import { t } from '$i18n'
import { PageToolbar } from '$common/app-bar/common/page-toolbar'

const StyledLoading = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.white,
  marginLeft: 10,
}))

const TitleTypography = styled(Typography)`
  margin-right: 7px;
  flex-grow: 1;
`

const StyledDoneIcon = styled(DoneIcon)(({ theme }) => ({
  transition: theme.transitions.create('opacity'),
}))

interface Prop {
  title: string
  selectionCount: number
  selectionLimit: number
  skippable: boolean
  onBack: () => unknown
  onSkip: () => unknown
  onConfirm: () => never | Promise<never>
}

export function AppBarBackTitleSkippable({
  title,
  selectionCount,
  skippable,
  selectionLimit,
  onBack,
  onSkip,
  onConfirm,
}: Prop) {
  const [loading, setLoading] = useState(false)

  const handleConfirm = () => {
    if (loading) return

    setLoading(true)
    onConfirm().finally(() => setLoading(false))
  }

  const confirmDisabled = !skippable && selectionCount === 0
  const limitReached = selectionLimit === selectionCount

  return (
    <AppBar>
      <PageToolbar>
        <BackButton onClick={onBack} />

        <TitleTypography color="neutral900" variant="subtitle1">
          {title}
          {selectionCount !== 0 && (
            <TitleTypography
              color={limitReached ? 'error.light' : 'secondary700'}
              variant="subtitle1"
            >
              ({toLocale(selectionLimit)}/{toLocale(selectionCount)})
            </TitleTypography>
          )}
        </TitleTypography>

        {loading ? (
          <StyledLoading size={20} />
        ) : skippable && selectionCount === 0 ? (
          <Button onClick={onSkip}>
            <Typography color="secondary500" variant="subtitle1">
              {t('common.skip')}
            </Typography>
          </Button>
        ) : (
          <IconButton onClick={handleConfirm} disabled={confirmDisabled}>
            <StyledDoneIcon
              sx={{ color: 'primary500' }}
              style={{
                opacity: confirmDisabled ? 0.4 : 1,
              }}
            />
          </IconButton>
        )}
      </PageToolbar>
    </AppBar>
  )
}
